/**
 * Array Utilities - Type Safety
 */

/**
 * Safely convert unknown value to typed array
 * Prevents "y.sort is not a function" errors
 * 
 * Handles:
 * - null/undefined → []
 * - Array → pass through
 * - {items: [...]} → extract items
 * - Other → []
 * 
 * @param value - Unknown value from API or state
 * @returns Typed array (empty if invalid)
 */
export function toArray<T>(value: unknown): T[] {
  // Already an array
  if (Array.isArray(value)) {
    return value as T[];
  }
  
  // Null or undefined
  if (value == null) {
    return [];
  }
  
  // API response with items wrapper: {items: [...]}
  if (typeof value === 'object' && 'items' in value) {
    const obj = value as { items?: unknown };
    if (Array.isArray(obj.items)) {
      return obj.items as T[];
    }
  }
  
  // API response with data wrapper: {data: [...]}
  if (typeof value === 'object' && 'data' in value) {
    const obj = value as { data?: unknown };
    if (Array.isArray(obj.data)) {
      return obj.data as T[];
    }
  }
  
  // Invalid type - return empty array
  return [];
}

/**
 * Safe number conversion
 * @param value - Unknown value
 * @param defaultValue - Default if invalid (default: 0)
 * @returns Number or default
 */
export function toNumber(value: unknown, defaultValue: number = 0): number {
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
}

/**
 * Calculate CTR safely (avoid division by zero)
 * @param clicks - Number of clicks
 * @param views - Number of views
 * @returns CTR percentage (0 if no views)
 */
export function calculateCTR(clicks: number, views: number): number {
  const c = toNumber(clicks, 0);
  const v = toNumber(views, 0);
  
  if (v === 0) return 0;
  
  return Number((100 * c / v).toFixed(2));
}
